import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Trash2, Loader2, Sparkles, BookOpen, ArrowLeft } from "lucide-react";

const STATUS = {
  published: ["Live", "bg-[#C2414F] border-[#C2414F] text-[#F2E8DF]"],
  changed: ["Edits not yet live", "border-[#D9B47A] text-[#D9B47A]"],
  draft: ["Draft · not live", "border-[#8F8176] text-[#CFC2B6]"],
  generating: ["Preparing…", "border-[#E8838E] text-[#E8838E]"],
  empty: ["Empty", "border-[#8F8176] text-[#A8998C]"],
};
const input = "w-full bg-[#0A0706] border border-[#D9B47A]/20 px-3 py-2.5 text-[15px] text-[#F2E8DF] placeholder:text-[#8F8176] focus:outline-none focus:border-[#D9B47A]";
const small = "inline-flex items-center gap-1.5 rounded-full border border-[#D9B47A]/30 px-3 py-1.5 text-[12px] text-[#CFC2B6] hover:border-[#D9B47A] hover:text-[#F2E8DF] transition-colors";

const Chip = ({ s }) => {
  const [label, cls] = STATUS[s] ?? STATUS.empty;
  return <span className={`inline-block rounded-full border px-3 py-1 text-[12px] whitespace-nowrap ${cls}`}>{label}</span>;
};

const move = (arr, i, d) => {
  const j = i + d;
  if (j < 0 || j >= arr.length) return arr;
  const a = [...arr];
  [a[i], a[j]] = [a[j], a[i]];
  return a;
};

// Up / down / delete controls for one item in an editable list.
const Row = ({ list, i, set, testId }) => (
  <div className="flex gap-1.5 shrink-0">
    <button type="button" aria-label="Move up" onClick={() => set(move(list, i, -1))} className={small}><ArrowUp className="w-3.5 h-3.5" /></button>
    <button type="button" aria-label="Move down" onClick={() => set(move(list, i, 1))} className={small}><ArrowDown className="w-3.5 h-3.5" /></button>
    <button type="button" data-testid={testId} aria-label="Remove" onClick={() => set(list.filter((_, k) => k !== i))} className={`${small} hover:!border-[#E8838E] hover:!text-[#E8838E]`}><Trash2 className="w-3.5 h-3.5" /></button>
  </div>
);

const Section = ({ title, onAdd, addLabel, testId, children }) => (
  <section data-testid={testId} className="mt-10">
    <div className="flex items-center justify-between gap-4 border-b border-[#D9B47A]/15 pb-3">
      <h3 className="font-tt-mono text-[12px] uppercase tracking-[0.25em] text-[#D9B47A]">{title}</h3>
      {onAdd && <button type="button" data-testid={`${testId}-add`} onClick={onAdd} className={small}><Plus className="w-3.5 h-3.5" /> {addLabel}</button>}
    </div>
    {children}
  </section>
);

const Cards = ({ cards, set }) => (
  <Section title={`Teaching cards (${cards.length})`} testId="qa-cards" addLabel="Card" onAdd={() => set([...cards, { title: "", text: "" }])}>
    {cards.map((c, i) => (
      <div key={i} data-testid={`qa-card-${i}`} className="mt-4 border border-[#D9B47A]/15 p-4 space-y-2">
        <div className="flex gap-2"><input value={c.title} maxLength={140} onChange={(e) => set(cards.map((x, k) => (k === i ? { ...x, title: e.target.value } : x)))} placeholder="Card title" className={input} /><Row list={cards} i={i} set={set} testId={`qa-card-remove-${i}`} /></div>
        <textarea value={c.text} maxLength={900} rows={3} onChange={(e) => set(cards.map((x, k) => (k === i ? { ...x, text: e.target.value } : x)))} placeholder="The teaching, in Ariel's voice" className={input} />
      </div>
    ))}
  </Section>
);

const Steps = ({ steps, set }) => (
  <div className="mt-3 space-y-2">
    {steps.map((s, i) => (
      <div key={i} className="border-l-2 border-[#E8838E]/40 pl-3 space-y-2">
        <div className="flex gap-2">
          <input value={s.title} maxLength={140} onChange={(e) => set(steps.map((x, k) => (k === i ? { ...x, title: e.target.value } : x)))} placeholder={`Step ${i + 1} title`} className={input} />
          <input type="number" min={0} max={3600} value={s.seconds} onChange={(e) => set(steps.map((x, k) => (k === i ? { ...x, seconds: Math.max(0, Math.min(3600, Number(e.target.value) || 0)) } : x)))} aria-label="Seconds" title="Timer in seconds (0 = no timer)" className={`${input} !w-24`} />
          <Row list={steps} i={i} set={set} />
        </div>
        <textarea value={s.text} maxLength={1200} rows={2} onChange={(e) => set(steps.map((x, k) => (k === i ? { ...x, text: e.target.value } : x)))} placeholder="Instruction" className={input} />
      </div>
    ))}
    <button type="button" onClick={() => set([...steps, { title: "", text: "", seconds: 60 }])} className={small}><Plus className="w-3.5 h-3.5" /> Step</button>
  </div>
);

const Practices = ({ practices, set }) => (
  <Section title={`Guided practices (${practices.length})`} testId="qa-practices" addLabel="Practice" onAdd={() => set([...practices, { title: "", mode: "solo", intro: "", steps: [] }])}>
    {practices.map((p, i) => {
      const upd = (patch) => set(practices.map((x, k) => (k === i ? { ...x, ...patch } : x)));
      return (
        <div key={i} data-testid={`qa-practice-${i}`} className="mt-4 border border-[#D9B47A]/15 p-4 space-y-2">
          <div className="flex gap-2">
            <input value={p.title} maxLength={160} onChange={(e) => upd({ title: e.target.value })} placeholder="Practice title" className={input} />
            <select value={p.mode} onChange={(e) => upd({ mode: e.target.value })} className={`${input} !w-32`} aria-label="Solo or partner"><option value="solo">Solo</option><option value="partner">Partner</option></select>
            <Row list={practices} i={i} set={set} />
          </div>
          <textarea value={p.intro} maxLength={900} rows={2} onChange={(e) => upd({ intro: e.target.value })} placeholder="Introduction and preparation" className={input} />
          <Steps steps={p.steps} set={(steps) => upd({ steps })} />
        </div>
      );
    })}
  </Section>
);

const Reflections = ({ list, set }) => (
  <Section title={`Reflection prompts (${list.length})`} testId="qa-reflections" addLabel="Prompt" onAdd={() => set([...list, ""])}>
    {list.map((q, i) => (
      <div key={i} className="mt-3 flex gap-2">
        <textarea value={q} rows={2} maxLength={500} onChange={(e) => set(list.map((x, k) => (k === i ? e.target.value : x)))} placeholder="Journaling question" className={input} />
        <Row list={list} i={i} set={set} />
      </div>
    ))}
  </Section>
);

const CheckinEdit = ({ checkin, set }) => (
  <Section title="Closing check-in" testId="qa-checkin">
    <input data-testid="qa-checkin-question" value={checkin.question} maxLength={300} onChange={(e) => set({ ...checkin, question: e.target.value })} placeholder="What did you feel?" className={`${input} mt-4`} />
    <label className="block mt-3 text-[13px] text-[#A8998C]">Answer options, one per line (people can also write their own)</label>
    <textarea data-testid="qa-checkin-options" rows={4} value={checkin.options.join("\n")} onChange={(e) => set({ ...checkin, options: e.target.value.split("\n").slice(0, 8) })} className={`${input} mt-2`} />
  </Section>
);

const clean = (d) => ({ ...d, reflections: d.reflections.filter((q) => q.trim()), checkin: { ...d.checkin, options: d.checkin.options.map((o) => o.trim()).filter(Boolean) } });

const CFG = {
  quantum: { api: "/admin/quantum", book: "Book II", testId: "quantum-admin", introHint: "How Quantum Intimacy deepens this gate, in Ariel's voice",
    about: "Quantum Intimacy is organised by the 13 Gates. Each gate’s Quantum layer holds Book II teaching cards, guided practices, reflections and a closing check-in." },
  gates13: { api: "/admin/gates13", book: "Book I", testId: "gates13-admin", introHint: "Ariel's welcome into this gate", prepareAll: true,
    about: "13 Gates to Ecstasy walks each gate as a short interactive journey. Ariel distils each gate’s Book I chapter and Mastery Guide into teaching cards, guided practices, reflections and a closing check-in." },
};

const Editor = ({ n, cfg, http, withRefresh, onBack, onChanged }) => {
  const [doc, setDoc] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState("");
  const [source, setSource] = useState(null);
  const load = useCallback(() => withRefresh(() => http.get(`${cfg.api}/gates/${n}`)).then(({ data }) => {
    setDoc(data);
    setDraft(data.draft);
    setDirty(false);
    return data;
  }), [n, http, withRefresh, cfg.api]);
  useEffect(() => {
    load().catch(() => toast.error("Could not open this gate."));
  }, [load]);
  useEffect(() => {
    if (doc?.status !== "generating") return undefined;
    const t = setInterval(() => load().then((d) => { if (d.status !== "generating") onChanged(); }).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [doc?.status, load, onChanged]);
  const set = (k) => (v) => { setDraft((d) => ({ ...d, [k]: v })); setDirty(true); };
  const act = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "save" || kind === "publish") await withRefresh(() => http.put(`${cfg.api}/gates/${n}`, clean(draft)));
      if (kind === "publish") await withRefresh(() => http.post(`${cfg.api}/gates/${n}/publish`));
      if (kind === "unpublish") await withRefresh(() => http.post(`${cfg.api}/gates/${n}/unpublish`));
      if (kind === "generate") await withRefresh(() => http.post(`${cfg.api}/gates/${n}/generate`));
      toast.success({ save: "Draft saved. Initiates still see the live version.", publish: "Published. Initiates now see this gate.", unpublish: "Taken offline.", generate: "Ariel is preparing a fresh draft…" }[kind]);
      await load();
      onChanged();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "That didn’t work. Please try again.");
    } finally {
      setBusy("");
    }
  };
  const openSource = (c) => (source?.n === c ? setSource(null) : withRefresh(() => http.get(`${cfg.api}/source/${c}`)).then(({ data }) => setSource(data)).catch(() => toast.error("Could not open the book text.")));
  if (!doc || !draft) return <Loader2 className="mt-10 w-5 h-5 animate-spin text-[#D9B47A]" />;
  const btn = "inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 font-tt-mono text-[11px] uppercase tracking-[0.2em] transition-colors disabled:opacity-50";
  return (
    <div data-testid="qa-editor" className="mt-8">
      <button data-testid="qa-back" onClick={onBack} className="inline-flex items-center gap-2 text-sm text-[#A8998C] hover:text-[#F2E8DF]"><ArrowLeft className="w-4 h-4" /> All gates</button>
      <div className="mt-5 flex flex-wrap items-center gap-3"><p className="font-display text-3xl leading-tight">Gate {n + 1} · {doc.name}</p><Chip s={doc.status} />{dirty && <span data-testid="qa-dirty" className="text-[12px] text-[#E8838E]">Unsaved changes</span>}</div>
      <p className="mt-2 text-[14px] text-[#A8998C]">{doc.line}</p>
      {doc.error && <p className="mt-2 text-sm text-[#E8838E]">{doc.error}</p>}
      <div className="sticky top-0 z-10 -mx-2 px-2 py-4 bg-[#0A0706]/95 backdrop-blur flex flex-wrap gap-2 border-b border-[#D9B47A]/15">
        <button data-testid="qa-save" onClick={() => act("save")} disabled={!!busy || doc.status === "generating"} className={`${btn} border border-[#D9B47A]/50 text-[#D9B47A] hover:bg-[#D9B47A] hover:text-[#0A0706]`}>{busy === "save" && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save draft</button>
        <button data-testid="qa-publish" onClick={() => act("publish")} disabled={!!busy || doc.status === "generating"} className={`${btn} bg-[#C2414F] text-[#F2E8DF] hover:bg-[#D9B47A] hover:text-[#0A0706]`}>{busy === "publish" && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {doc.has_published ? "Publish changes" : "Publish"}</button>
        {doc.has_published && <button data-testid="qa-unpublish" onClick={() => act("unpublish")} disabled={!!busy} className={`${btn} border border-[#8F8176] text-[#CFC2B6] hover:border-[#E8838E] hover:text-[#E8838E]`}>Take offline</button>}
        <button data-testid="qa-generate" onClick={() => window.confirm(`Replace this draft with a fresh preparation from ${cfg.book}? Your live version is not affected until you publish.`) && act("generate")} disabled={!!busy || doc.status === "generating"} className={`${btn} border border-[#E8838E]/50 text-[#E8838E] hover:bg-[#E8838E] hover:text-[#0A0706]`}>{doc.status === "generating" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Re-prepare from book</button>
      </div>
      <div data-testid="qa-sources" className="mt-5 flex flex-wrap items-center gap-2">
        <span className="text-[13px] text-[#A8998C] flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5 text-[#D9B47A]" /> {cfg.book} source:</span>
        {(doc.sources.length ? doc.sources : doc.chapters).map((c) => (
          <button key={c.n} data-testid={`qa-source-${c.n}`} onClick={() => openSource(c.n)} className={`${small} ${source?.n === c.n ? "!border-[#D9B47A] !text-[#F2E8DF]" : ""}`}>{c.title.replace(/:.*$/, "")}</button>
        ))}
      </div>
      {source && <pre data-testid="qa-source-text" className="mt-4 max-h-96 overflow-auto whitespace-pre-wrap border border-[#D9B47A]/15 bg-[#0D0908] p-4 text-[13px] leading-relaxed text-[#CFC2B6] font-sans">{source.title}{"\n\n"}{source.text}</pre>}
      <Section title="Gate introduction" testId="qa-intro">
        <textarea data-testid="qa-intro-text" value={draft.intro ?? ""} maxLength={1200} rows={3} onChange={(e) => set("intro")(e.target.value)} placeholder={cfg.introHint} className={`${input} mt-4`} />
      </Section>
      <Cards cards={draft.cards} set={set("cards")} />
      <Practices practices={draft.practices} set={set("practices")} />
      <Reflections list={draft.reflections} set={set("reflections")} />
      <CheckinEdit checkin={draft.checkin} set={set("checkin")} />
    </div>
  );
};

// Admin: review, edit and publish the interactive gate journeys of either app (kind: quantum | gates13).
export default function QuantumSegmentsAdmin({ http, withRefresh, kind = "quantum" }) {
  const cfg = CFG[kind];
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    withRefresh(() => http.get(`${cfg.api}/gates`)).then(({ data }) => setList(Array.isArray(data) ? data : [])).catch(() => toast.error("Could not load gates."));
  }, [http, withRefresh, cfg.api]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (!(list ?? []).some((c) => c.status === "generating")) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [list, load]);
  if (open !== null) return <Editor n={open} cfg={cfg} http={http} withRefresh={withRefresh} onBack={() => { setOpen(null); load(); }} onChanged={load} />;
  const empty = (list ?? []).filter((c) => c.status === "empty" || (!c.cards && !c.practices && c.status !== "generating")).length;
  const prepareAll = async () => {
    try {
      const { data } = await withRefresh(() => http.post(`${cfg.api}/gates/generate-all`));
      toast.success(`Ariel is preparing ${data?.preparing?.length ?? 0} gates. This takes a few minutes.`);
      load();
    } catch {
      toast.error("That didn’t work. Please try again.");
    }
  };
  const live = (list ?? []).filter((c) => c.status === "published" || c.status === "changed").length;
  const pending = (list ?? []).filter((c) => (c.status === "draft" || c.status === "changed") && (c.cards || c.practices)).length;
  const publishAll = async () => {
    if (!window.confirm(`Publish ${pending} gate${pending === 1 ? "" : "s"} with drafts or unpublished edits? Initiates will see them straight away.`)) return;
    setBusy(true);
    try {
      const { data } = await withRefresh(() => http.post(`${cfg.api}/gates/publish-all`));
      const n = data?.published?.length ?? 0;
      const skipped = data?.skipped?.length ?? 0;
      toast.success(`${n} gate${n === 1 ? "" : "s"} published.${skipped ? ` ${skipped} empty draft${skipped === 1 ? " was" : "s were"} skipped.` : ""}`);
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "That didn’t work. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={cfg.testId} className="mt-10">
      <p className="text-[#CFC2B6] max-w-2xl">{cfg.about} Review and edit each draft, then publish it. <span className="text-[#D9B47A]">{live} of {list?.length ?? 13} gates live.</span></p>
      {cfg.prepareAll && empty > 0 && <button data-testid="qa-prepare-all" onClick={prepareAll} className="mt-6 mr-3 inline-flex items-center justify-center gap-2 rounded-full border border-[#E8838E]/50 text-[#E8838E] px-5 py-3 font-tt-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#E8838E] hover:text-[#0A0706] transition-colors"><Sparkles className="w-3.5 h-3.5" /> Prepare all with Ariel ({empty})</button>}
      {pending > 0 && <button data-testid="qa-publish-all" onClick={publishAll} disabled={busy} className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#C2414F] text-[#F2E8DF] px-5 py-3 font-tt-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors disabled:opacity-50">{busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Publish all ({pending})</button>}
      {!pending && list && <p data-testid="qa-publish-all-none" className="mt-2 text-[13px] text-[#A8998C]">Every prepared gate is already live.</p>}
      <ul className="mt-8 border-t border-[#D9B47A]/15">
        {(list ?? []).map((c) => (
          <li key={c.g}>
            <button data-testid={`qa-gate-${c.g}`} onClick={() => setOpen(c.g)} className="w-full text-left grid grid-cols-12 gap-3 items-center py-4 px-2 border-b border-[#D9B47A]/15 hover:bg-[#120C0A] transition-colors">
              <span className="col-span-12 sm:col-span-8 min-w-0">
                <span className="block font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#A8998C] truncate">Gate {String(c.g + 1).padStart(2, "0")} · {c.cards} cards · {c.practices} practices</span>
                <span className="block font-display text-xl truncate">{c.name}</span>
              </span>
              <span className="col-span-12 sm:col-span-4 sm:text-right"><Chip s={c.status} /></span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
